/**
 * src/shell/portal/components/media/VideoEmbedPlayer.tsx
 *
 * Sovereign Responsive Video Embed Player for YouTube and Vimeo.
 * Fully compliant with Sprint 11 Theme Engine Token System (zero bare hex codes).
 */

import React, { useState } from 'react';
import type { VideoPayload, SessionState } from '../../../../domain/media/media-feed-contracts.js';

export interface VideoEmbedPlayerProps {
  payload: VideoPayload;
  title: string;
  thumbnailUrl?: string;
  sessionState?: SessionState;
  autoplay?: boolean;
  onEnded?: () => void;
}

export const VideoEmbedPlayer: React.FC<VideoEmbedPlayerProps> = ({
  payload,
  title,
  thumbnailUrl,
  sessionState = 'prerecorded',
  autoplay = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  // Construct embed URL with parameters
  const buildEmbedUrl = () => {
    if (payload.provider === 'youtube') {
      const base = `https://www.youtube-nocookie.com/embed/${payload.platformId}`;
      const params = new URLSearchParams({
        autoplay: '1',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
      });
      if (payload.startTimeSec) params.set('start', String(payload.startTimeSec));
      if (payload.endTimeSec) params.set('end', String(payload.endTimeSec));
      return `${base}?${params.toString()}`;
    }

    if (payload.provider === 'vimeo') {
      const base = `https://player.vimeo.com/video/${payload.platformId}`;
      const params = new URLSearchParams({
        autoplay: '1',
        dnt: '1',
      });
      return `${base}?${params.toString()}`;
    }

    return payload.embedUrl;
  };

  const poster = thumbnailUrl || (payload.provider === 'youtube'
    ? `https://img.youtube.com/vi/${payload.platformId}/maxresdefault.jpg`
    : 'https://storage.googleapis.com/gtv-videos-bucket/sample/images/TearsOfSteel.jpg');

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        paddingTop: '56.25%', // 16:9 Aspect Ratio
        backgroundColor: 'var(--theme-bg-main, #090d16)',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
        border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
      }}
    >
      {/* State / Provider Badge */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        {sessionState === 'live' ? (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: 'var(--theme-status-danger-bg, rgba(239, 68, 68, 0.9))',
              color: 'var(--theme-status-danger-text, #ffffff)',
              fontSize: '10px',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              boxShadow: '0 0 10px rgba(239, 68, 68, 0.6)',
            }}
          >
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor', animation: 'pulse 1.5s infinite' }} />
            Live Now
          </span>
        ) : (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              backgroundColor: 'var(--theme-bg-sidebar, rgba(15, 23, 42, 0.85))',
              border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
              color: 'var(--theme-text-muted, #94a3b8)',
              fontSize: '10px',
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            📼 Replay
          </span>
        )}

        <span
          style={{
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: 'var(--theme-bg-card, rgba(0, 0, 0, 0.75))',
            border: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.06))',
            color: 'var(--theme-text-primary, #e2e8f0)',
            fontSize: '10px',
            fontWeight: 700,
          }}
        >
          {payload.provider === 'youtube' ? '▶ YouTube' : '🎬 Vimeo'}
        </span>
      </div>

      {/* Layer 1: Poster Frame with Click-to-Play */}
      {!isPlaying ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setIsPlaying(true);
          }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${poster})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Subtle Dark Overlay */}
          <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.35)' }} />

          {/* Central Play Button */}
          <button
            aria-label={`Play ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              setIsPlaying(true);
            }}
            style={{
              position: 'relative',
              zIndex: 2,
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'var(--theme-accent, #38bdf8)',
              border: '2px solid var(--theme-border-highlight, rgba(255, 255, 255, 0.8))',
              color: 'var(--theme-text-inverse, #0f172a)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(56, 189, 248, 0.5)',
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1.0)')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>
      ) : (
        /* Layer 2: Embedded Iframe */
        <iframe
          src={buildEmbedUrl()}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onLoad={() => setIsIframeLoaded(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            opacity: isIframeLoaded ? 1 : 0.8,
            transition: 'opacity 0.3s ease',
          }}
        />
      )}
    </div>
  );
};
